import { useContext, useEffect, useMemo, useState } from "react"
import BasePage from "../../../../components/layout/BasePage"
import BaseHeadBanner from "../../../../components/elements/BaseHeadBanner"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getSingleObjectData } from "../../api/apiCalls"
import { useParams } from "react-router-dom"
import ErrorPage from "../../../404/ErrorPage"
import Loading from "../../../../components/common/Loading"
import { ObjectType } from "../../types/types"
import { useGenerateQRCodeOfObjectMutation, useUpdateObjectMutation } from "../../api/services"
import { getBillByObject, getOfferByObject } from "../../../Buchhaltung/api/apiCalls"
import { returnAddressString, useDelayWhenSearchWithPagination } from "../../../../services/functions"
import ObjectDetailPartRight from "./ObjectDetailPartRight"
import ObjectDetailPartLeft from "./ObjectDetailPartLeft"
import ObjectDetailHero from "./ObjectDetailHero"
import ObjectDetailMasterData from "./ObjectDetailMasterData"
import EditObjectMasterDataModal, { EDIT_OBJECT_MASTER_DATA_MODAL_ID } from "./EditObjectMasterDataModal"
import ObjectDetailActions from "./ObjectDetailActions"
import EditLvModal from "../../components/EditLvModal"
import ServiceCatalogModal from "../../components/ServiceCatalogModal"
import BasePreview from "../../../../components/elements/BasePreview"
import { API, GeneralQueryKeys } from "../../../../api/constants"
import EditOBVModal from "../../components/EditOBVModal"
import QrCodeModal from "../../components/QrCodeModal"
import { Customer } from "../../../Kunden/types/Types"
import BaseGrowBody from "../../../../components/elements/BaseGrowBody"
import ObjectDetailStatistics from "./ObjectDetailStatistics"
import { getCostCenterNames, getObjectStatistics } from "../../../Dashboard/api/apiCalls"
import { getCustomerNamesOnly } from "../../../Kunden/api/apiCalls"
import BaseFolderStructurModal from "../../../../components/elements/BaseFolderStructurModal"
import { ModalContext } from "../../../../components/contexts/ModalContext"
import KeyManagementModal from "../../components/KeyManagementModal"
import { SidePanelContext } from "../../../../components/contexts/SidePanelContext"

export default function ObjektDetailPage() {
    const sidePanelCon = useContext(SidePanelContext)
    const { id } = useParams()
    const queryKeySingleObject = GeneralQueryKeys.OBJECT_SINGLE + (id ?? "")
    const { data, error, isLoading } = useQuery({
        queryKey: [queryKeySingleObject],
        queryFn: () => getSingleObjectData(id ?? ""),
        enabled: !!id,
    })
    const [displayedObject, setdisplayedObject] = useState<ObjectType>()
    useEffect(() => setdisplayedObject(data), [data])

    // Get Customer Names For Change Customer - Data Query
    const [searchTerm, setsearchTerm] = useState<string>()
    const searchParams = useDelayWhenSearchWithPagination(searchTerm ?? "")
    const queryKey = GeneralQueryKeys.CUSTOMER_NAMES_ONLY + 1 + searchParams
    const { error: allcustomerError, data: allcustomerData } = useQuery({
        queryKey: [queryKey],
        queryFn: () => getCustomerNamesOnly(searchParams),
    })

    // get all costcenter
    const { error: costcenterError, data: costcenterData } = useQuery({
        queryKey: [GeneralQueryKeys.COSTCENTER_NAMES],
        queryFn: getCostCenterNames,
    })

    // Offers of objects
    const { data: objectsOffer, error: objErrOffer } = useQuery({
        queryKey: [GeneralQueryKeys.OFFER_BY_OBJECT + id],
        queryFn: () => getOfferByObject(id || ""),
    })

    // Bills of objects
    const { data: objectsBill, error: objErrBill } = useQuery({
        queryKey: [GeneralQueryKeys.BILL_BY_OBJECT + id],
        queryFn: () => getBillByObject(id || ""),
    })

    // STATISTICS OF OBJECT
    const { data: objectStatistics, error: errorObjectStatistics } = useQuery({
        queryKey: [GeneralQueryKeys.OBJECT_STATISTICS + id],
        queryFn: () => getObjectStatistics(id || ""),
    })

    // get preview of pdf file
    const [selectedS3Key, setselectedS3Key] = useState<string>()
    const handlePreviewDocument = (s3Key: string) => {
        setselectedS3Key(s3Key)
        sidePanelCon?.trigger(6)
    }
    const objectDownloadUrl = useMemo(() => {
        return error || isLoading || !selectedS3Key ? undefined : API + `/object/${id}/download?s3Key=${selectedS3Key}`
    }, [error, isLoading, selectedS3Key, id])

    const queryClient = useQueryClient()

    // generate QR Code
    const { mutate: generateQRcode, status: statusQRCodeGeneration } = useGenerateQRCodeOfObjectMutation(queryClient)

    // update object
    const { mutate: updateObjectQuery } = useUpdateObjectMutation(queryClient)
    const modalCon = useContext(ModalContext)
    const handleUpdateObject = () => {
        if (displayedObject) {
            updateObjectQuery({
                id: id ?? "",
                body: displayedObject,
            })
            if (modalCon?.open) modalCon.trigger(0)
        }
    }
    const handleCancelEdit = () => {
        setdisplayedObject(data)
    }

    return (
        <BasePage>
            {error && <ErrorPage />}
            {isLoading && <Loading />}
            {data && displayedObject && !error && !isLoading && (
                <>
                    <div className="w-full h-full overflow-hidden flex flex-col gap-4">
                        <BaseHeadBanner
                            title={(displayedObject.objectIndication || returnAddressString(data.adress)) + " | Nummer: " + (data?.nr ?? "404")}
                            button={
                                <ObjectDetailActions
                                    lvdoesnotexist={!data.listOfServices}
                                    obvdoesexist={!data.serviceContract?.created}
                                    ranking={displayedObject.ranking}
                                />
                            }
                        />
                        <BaseGrowBody classProps="flex flex-col gap-4 pb-4 overflow-y-scroll">
                            <div className="flex flex-col items-start gap-4 lg:flex-row">
                                <ObjectDetailHero
                                    objectData={displayedObject}
                                    customerOfObject={(displayedObject.customer_id as Customer)?.customer?.name}
                                />
                                <div className="w-full shrink-0 lg:w-[min(46%,36rem)]">
                                    <ObjectDetailMasterData
                                        objectData={displayedObject}
                                        costCenterData={costcenterData}
                                        onEdit={() => modalCon?.trigger(EDIT_OBJECT_MASTER_DATA_MODAL_ID)}
                                    />
                                </div>
                            </div>
                            <ObjectDetailPartRight
                                setselectedS3Key={setselectedS3Key}
                                handleDownload={() => {}}
                                setfileToDelete={() => {}}
                                documents={data.s3Documents}
                            />
                            <ObjectDetailPartLeft
                                id={data._id}
                                customerOfObject={(displayedObject.customer_id as Customer)?.customer?.name}
                                customerId={(displayedObject.customer_id as Customer)?._id ?? ""}
                                objectsOffer={objectsOffer}
                                objectsBill={objectsBill}
                                objectAdress={returnAddressString(displayedObject.adress)}
                            />
                            <ObjectDetailStatistics statistics={objectStatistics} />
                        </BaseGrowBody>
                    </div>
                    <EditObjectMasterDataModal
                        objectData={displayedObject}
                        setobjectData={setdisplayedObject}
                        costCenterData={costcenterData}
                        onSave={handleUpdateObject}
                        onCancel={handleCancelEdit}
                        customerOfObject={(displayedObject.customer_id as Customer)?.customer?.name}
                        customerData={allcustomerData ?? []}
                        searchTermCustomer={searchTerm}
                        setsearchTermCustomer={setsearchTerm}
                    />
                    <EditLvModal modalId={5} objectId={data._id} data={data.listOfServices} />
                    <ServiceCatalogModal modalId={11} objectId={data._id} />
                    <BasePreview title={"Dokumentvorschau"} sidePanelId={6} previewType={"document"} content={objectDownloadUrl} />
                    {!data.serviceContract?.created && (
                        <EditOBVModal
                            modalId={7}
                            objectId={data._id}
                            data={data.listOfServices}
                            object={data.adress?.street + " " + data.adress?.nr + ", " + data.adress?.plz + " " + data.adress?.city}
                            customer={data.customer_id}
                            closeModal={() => modalCon?.trigger(7)}
                        />
                    )}
                    <QrCodeModal
                        modalId={8}
                        qrcode={data.qrCodeID}
                        adress={data.adress?.street + data.adress?.nr + "." + data.adress?.plz + data.adress?.city}
                        generateQRcode={() => generateQRcode({ id: data._id })}
                        statusQRCodeGeneration={statusQRCodeGeneration}
                    />
                    <BaseFolderStructurModal
                        modalId={9}
                        title={data.objectIndication}
                        type="object"   
                        data={data.folders}
                        id={data._id}
                        queryKey={queryKeySingleObject}
                        documents={data.s3Documents}
                        handlePreviewDocument={handlePreviewDocument}
                    />
                    <KeyManagementModal modalId={10} queryKey={queryKeySingleObject} objectId={data._id} keycollections={data.keyCollection} />
                </>
            )}
        </BasePage>
    )
}
